import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";

const TYPES = ['NONE', 'RANDOM_TEAMMATES', 'PLAYER_CHOICE', 'IMAGE_QUIZ', 'HOST_COUNTER', 'TIMING_CHALLENGE', 'HOST_JUDGED'];
const NUM = ['displayNumber', 'timerSeconds', 'attempts', 'secondaryPlayerCount', 'targetSeconds', 'toleranceSeconds', 'targetCount'];

// Challenge Game Master cockpit: catalog, tonight's wheel pool, editor, random/manual draw.
export const ChallengeGameMaster = ({ sessionId, session, apiCall, playedIds = [], onDrawn, catalogOnly = false }) => {
  const [games, setGames] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [allowRepeats, setAllowRepeats] = useState(false);
  const [showSelect, setShowSelect] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const EMPTY_NEW = { title: '', description: '', successCriteria: '', interactionType: 'NONE', timerSeconds: '', attempts: '', secondaryPlayerCount: '' };
  const [createForm, setCreateForm] = useState(EMPTY_NEW);

  const load = async () => {
    try { const r = await apiCall('/api/challenges/games'); setGames(r.games || []); } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const activeGames = games.filter(g => g.enabled && g.status === 'ACTIVE');
  const enabledIds = Array.isArray(session?.challengeEnabledIds) && session.challengeEnabledIds.length
    ? session.challengeEnabledIds : activeGames.map(g => g.id);
  const inWheel = (id) => enabledIds.includes(id);
  const wheelCount = activeGames.filter(g => inWheel(g.id)).length;

  const savePool = async (ids) => {
    try { await apiCall('/api/challenges/session-pool', 'POST', { session_id: sessionId, challenge_enabled_ids: ids }); }
    catch (e) { alert(`Pool speichern fehlgeschlagen: ${e.message}`); }
  };
  const togglePool = (id) => savePool(inWheel(id) ? enabledIds.filter(x => x !== id) : [...enabledIds, id]);

  const toggleGlobal = async (g) => {
    setBusy(true);
    try { await apiCall(`/api/challenges/games/${g.id}`, 'PUT', { fields: { enabled: !g.enabled } }); await load(); }
    catch (e) { alert(e.message); }
    setBusy(false);
  };

  const openEdit = (g) => { setEditing(g.id); setForm({ ...g, settings: JSON.stringify(g.settings || {}), media: JSON.stringify(g.media || []) }); };
  const saveEdit = async () => {
    setBusy(true);
    try {
      const fields = {};
      ['title', 'description', 'successCriteria', 'interactionType', 'hostNotes', ...NUM].forEach(k => { if (form[k] !== undefined) fields[k] = form[k] === '' ? null : form[k]; });
      NUM.forEach(k => { if (fields[k] != null) fields[k] = Number(fields[k]); });
      try { fields.settings = JSON.parse(form.settings || '{}'); fields.media = JSON.parse(form.media || '[]'); }
      catch { alert('Settings/Media müssen gültiges JSON sein'); setBusy(false); return; }
      await apiCall(`/api/challenges/games/${editing}`, 'PUT', { fields });
      setEditing(null); await load();
    } catch (e) { alert(`Speichern fehlgeschlagen: ${e.message}`); }
    setBusy(false);
  };

  const draw = async (challengeId = null) => {
    setBusy(true);
    try {
      const r = await apiCall('/api/challenges/start-round', 'POST', { session_id: sessionId, challenge_id: challengeId, allow_repeats: allowRepeats });
      setShowSelect(false); onDrawn && onDrawn(r);
    } catch (e) { alert(e.message); }
    setBusy(false);
  };

  const createGame = async () => {
    if (!createForm.title.trim()) { alert('Bitte einen Titel eingeben'); return; }
    setBusy(true);
    try {
      const fields = { ...createForm };
      ['timerSeconds', 'attempts', 'secondaryPlayerCount'].forEach(k => { fields[k] = fields[k] === '' ? null : Number(fields[k]); });
      await apiCall('/api/challenges/games', 'POST', { fields });
      setCreateForm(EMPTY_NEW); setShowCreate(false); await load();
    } catch (e) { alert(`Anlegen fehlgeschlagen: ${e.message}`); }
    setBusy(false);
  };

  const duplicateGame = async (g) => {
    setBusy(true);
    try { await apiCall(`/api/challenges/games/${g.id}/duplicate`, 'POST'); await load(); }
    catch (e) { alert(`Duplizieren fehlgeschlagen: ${e.message}`); }
    setBusy(false);
  };

  const deleteGame = async (g) => {
    if (!window.confirm(`Minigame „${g.title}" wirklich löschen? Das kann nicht rückgängig gemacht werden.`)) return;
    setBusy(true);
    try { if (editing === g.id) setEditing(null); await apiCall(`/api/challenges/games/${g.id}`, 'DELETE'); await load(); }
    catch (e) { alert(`Löschen fehlgeschlagen: ${e.message}`); }
    setBusy(false);
  };

  const nf = ({ k, label, type = 'text', rows }) => (
    <label className="block text-xs text-rose-200/60">
      {label}
      {rows ? (
        <textarea rows={rows} value={createForm[k] ?? ''} onChange={e => setCreateForm(f => ({ ...f, [k]: e.target.value }))} className="mt-1 w-full p-2 bg-[#0a0608] border border-white/15 rounded text-sm text-rose-50" data-testid={`new-${k}`} />
      ) : (
        <input type={type} value={createForm[k] ?? ''} onChange={e => setCreateForm(f => ({ ...f, [k]: e.target.value }))} className="mt-1 w-full h-8 px-2 bg-[#0a0608] border border-white/15 rounded text-sm text-rose-50" data-testid={`new-${k}`} />
      )}
    </label>
  );

  const ef = ({ k, label, type = 'text', rows }) => (
    <label className="block text-xs text-rose-200/60">
      {label}
      {rows ? (
        <textarea rows={rows} value={form[k] ?? ''} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} className="mt-1 w-full p-2 bg-[#0a0608] border border-white/15 rounded text-sm text-rose-50" data-testid={`edit-${k}`} />
      ) : (
        <input type={type} value={form[k] ?? ''} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} className="mt-1 w-full h-8 px-2 bg-[#0a0608] border border-white/15 rounded text-sm text-rose-50" data-testid={`edit-${k}`} />
      )}
    </label>
  );

  return (
    <div className="space-y-4" data-testid="challenge-game-master">
      <div className="p-3 rounded-lg bg-amber-900/20 border border-amber-500/30 flex flex-wrap items-center gap-3">
        {catalogOnly ? (
          <span className="text-sm font-bold text-amber-300" data-testid="catalog-count">GAMES: {games.length} · AKTIV: {activeGames.length}</span>
        ) : (
          <>
            <span className="text-sm font-bold text-amber-300" data-testid="wheel-count">HEUTE IM RAD: {wheelCount}</span>
            <label className="text-xs flex items-center gap-1 text-rose-200/70"><input type="checkbox" checked={allowRepeats} onChange={e => setAllowRepeats(e.target.checked)} data-testid="allow-repeats" /> Wiederholungen erlauben</label>
          </>
        )}
        <div className="ml-auto flex gap-2">
          <Button data-testid="new-game-btn" onClick={() => setShowCreate(s => !s)} className="bg-amber-600 hover:bg-amber-700">＋ NEUES GAME</Button>
          {!catalogOnly && (
            <>
              <Button data-testid="draw-random-btn" onClick={() => draw(null)} disabled={busy || wheelCount === 0} className="bg-purple-600 hover:bg-purple-700">🎡 CHALLENGE ZIEHEN</Button>
              <Button data-testid="select-manual-btn" onClick={() => setShowSelect(s => !s)} variant="outline" className="bg-white/5 border-white/15">📋 Challenge auswählen</Button>
            </>
          )}
        </div>
      </div>

      {showCreate && (
        <div className="p-4 rounded-lg bg-amber-950/30 border border-amber-500/30 space-y-3" data-testid="new-game-form">
          <div className="text-sm font-bold text-amber-300">Neues Minigame anlegen <span className="font-normal text-rose-200/50">— startet pausiert, du aktivierst es danach</span></div>
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">{nf({ k: 'title', label: 'Titel *' })}</div>
            <div className="col-span-2">{nf({ k: 'description', label: 'Beschreibung', rows: 2 })}</div>
            <div className="col-span-2">{nf({ k: 'successCriteria', label: 'Erfolgskriterium', rows: 2 })}</div>
            <label className="block text-xs text-rose-200/60">Interaction Type
              <select value={createForm.interactionType} onChange={e => setCreateForm(f => ({ ...f, interactionType: e.target.value }))} className="mt-1 w-full h-8 px-2 bg-[#0a0608] border border-white/15 rounded text-sm" data-testid="new-interactionType">
                {TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </label>
            {nf({ k: 'secondaryPlayerCount', label: 'Anzahl Mitspieler', type: 'number' })}
            {nf({ k: 'timerSeconds', label: 'Timer (Sek.)', type: 'number' })}
            {nf({ k: 'attempts', label: 'Versuche', type: 'number' })}
          </div>
          <div className="flex gap-2">
            <Button data-testid="new-game-save-btn" onClick={createGame} disabled={busy} className="bg-green-600 hover:bg-green-700">GAME ANLEGEN</Button>
            <Button onClick={() => { setShowCreate(false); setCreateForm(EMPTY_NEW); }} variant="outline" className="border-white/20">Abbrechen</Button>
          </div>
        </div>
      )}

      <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
        {games.map(g => {
          const played = playedIds.includes(g.id);
          const active = g.enabled && g.status === 'ACTIVE';
          return (
            <div key={g.id} data-testid={`game-card-${g.id}`} className={`p-3 rounded-lg border ${active ? 'bg-white/5 border-white/10' : 'bg-black/30 border-white/5 opacity-60'}`}>
              <div className="flex items-center gap-2">
                <span className="text-xs text-rose-200/40 w-6">#{g.displayNumber ?? '–'}</span>
                <span className="font-semibold text-sm flex-1 truncate">{g.title} {played && <span className="text-[10px] text-green-400 ml-1">✓ gespielt</span>}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10">{g.interactionType}{g.secondaryPlayerCount ? ` ×${g.secondaryPlayerCount}` : ''}{g.timerSeconds ? ` · ${g.timerSeconds}s` : ''}</span>
                {active && !catalogOnly && (
                  <button data-testid={`pool-toggle-${g.id}`} onClick={() => togglePool(g.id)} className={`text-[11px] px-2 py-1 rounded ${inWheel(g.id) ? 'bg-green-600/30 text-green-300' : 'bg-white/10 text-rose-200/60'}`}>
                    {inWheel(g.id) ? '🟢 IM RAD' : '⚫ HEUTE RAUS'}
                  </button>
                )}
                <button data-testid={`global-toggle-${g.id}`} onClick={() => toggleGlobal(g)} disabled={busy} title="Global aktiv/inaktiv" className="text-[11px] px-2 py-1 rounded bg-white/10">{active ? 'AKTIV' : 'PAUSIERT'}</button>
                <button data-testid={`edit-${g.id}`} onClick={() => editing === g.id ? setEditing(null) : openEdit(g)} className="text-sm px-2" title="Bearbeiten">✏️</button>
                <button data-testid={`duplicate-${g.id}`} onClick={() => duplicateGame(g)} disabled={busy} className="text-sm px-1" title="Duplizieren">⧉</button>
                <button data-testid={`delete-${g.id}`} onClick={() => deleteGame(g)} disabled={busy} className="text-sm px-1 text-rose-400 hover:text-rose-300" title="Löschen">🗑</button>
                {showSelect && active && !catalogOnly && (
                  <Button size="sm" data-testid={`start-${g.id}`} onClick={() => draw(g.id)} disabled={busy || (played && !allowRepeats)} className="bg-green-600 hover:bg-green-700 h-7 text-xs">START</Button>
                )}
              </div>
              {editing === g.id && (
                <div className="mt-3 grid grid-cols-2 gap-2" data-testid={`editor-${g.id}`}>
                  {ef({ k: 'title', label: 'Titel' })}{ef({ k: 'displayNumber', label: 'Display-Nr', type: 'number' })}
                  <div className="col-span-2">{ef({ k: 'description', label: 'Beschreibung', rows: 3 })}</div>
                  <div className="col-span-2">{ef({ k: 'successCriteria', label: 'Erfolgskriterium', rows: 2 })}</div>
                  {ef({ k: 'timerSeconds', label: 'Timer (Sek.)', type: 'number' })}{ef({ k: 'attempts', label: 'Versuche', type: 'number' })}
                  <label className="block text-xs text-rose-200/60">Interaction Type
                    <select value={form.interactionType || 'NONE'} onChange={e => setForm(f => ({ ...f, interactionType: e.target.value }))} className="mt-1 w-full h-8 px-2 bg-[#0a0608] border border-white/15 rounded text-sm" data-testid="edit-interactionType">
                      {TYPES.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </label>
                  {ef({ k: 'secondaryPlayerCount', label: 'Secondary Players', type: 'number' })}
                  {ef({ k: 'targetSeconds', label: 'Zielzeit (Sek.)', type: 'number' })}{ef({ k: 'toleranceSeconds', label: 'Toleranz (Sek.)', type: 'number' })}
                  {ef({ k: 'targetCount', label: 'Ziel-Anzahl (Counter/Quiz)', type: 'number' })}
                  <div className="col-span-2">{ef({ k: 'hostNotes', label: 'Host Notes', rows: 2 })}</div>
                  <div className="col-span-2">{ef({ k: 'settings', label: 'Settings (JSON)', rows: 2 })}</div>
                  <div className="col-span-2">{ef({ k: 'media', label: 'Medien/Bilder (JSON-Array)', rows: 3 })}</div>
                  <div className="col-span-2 flex gap-2">
                    <Button data-testid="editor-save-btn" onClick={saveEdit} disabled={busy} className="bg-blue-600 hover:bg-blue-700">SPEICHERN</Button>
                    <Button onClick={() => setEditing(null)} variant="outline" className="border-white/20">Abbrechen</Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
